import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import { Button, Col, Row, Space, Table, Typography, Card, Tooltip, Tag, Input, Select } from 'antd';
import { openNotification } from '../../../utils';
import { HolderOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckmarkCircle02Icon } from 'hugeicons-react';

import { listQuestions, listQuestionsUnpaged, deleteQuestion, reorderQuestions, cloneQuestion, updateQuestion } from '../services/questionService';
import { getReportFormatFunctionTags } from '../../report-formats/services/report-formatService';
import type { QuestionFormSuccessPayload } from './Form';
import PenIcon from 'assets/icons/pen.svg';
import QuestionActions from './QuestionActions';
import QuestionEditorDrawer from './QuestionEditorDrawer';
import ToolService from 'services/ToolService';
import AttributeService from 'services/AttributeService';
import { apiFetch } from 'services/apiClient';

const { Title, Text } = Typography;

type StaticFunctionTag = {
  name: string;
  value: string;
};

const mapStaticValuesToTags = (
  items: Array<{ name?: string; functionTag?: string; isActive?: boolean; active?: boolean }> | undefined,
): StaticFunctionTag[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  const map = new Map<string, StaticFunctionTag>();
  items.forEach((item) => {
    const activeFlag = item?.isActive ?? item?.active;
    if (activeFlag !== undefined && !activeFlag) {
      return;
    }
    const rawTag = String(item?.functionTag ?? '').trim();
    const fallbackName = String(item?.name ?? '').trim();
    const derivedTag = rawTag.length > 0 ? rawTag : fallbackName.replace(/\s+/g, '').toUpperCase();
    const normalizedTag = derivedTag.trim();
    if (!normalizedTag) {
      return;
    }
    const key = normalizedTag.toUpperCase();
    if (!map.has(key)) {
      map.set(key, {
        name: fallbackName || normalizedTag,
        value: normalizedTag,
      });
    }
  });
  return Array.from(map.values());
};

const mergeStaticFunctionTags = (
  ...groups: Array<StaticFunctionTag[] | undefined>
): StaticFunctionTag[] => {
  const map = new Map<string, StaticFunctionTag>();
  groups
    .filter(Boolean)
    .forEach((group) => {
      (group as StaticFunctionTag[]).forEach((item) => {
        const value = String(item?.value ?? '').trim();
        if (!value) {
          return;
        }
        const key = value.toUpperCase();
        if (!map.has(key)) {
          map.set(key, { name: item?.name ?? value, value });
        }
      });
    });
  return Array.from(map.values());
};

const normalizeFunctionTags = (items?: Array<string | null | undefined>): string[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  const map = new Map<string, string>();
  items.forEach((item) => {
    const raw = String(item ?? '').trim();
    if (!raw) {
      return;
    }
    const key = raw.toUpperCase();
    if (!map.has(key)) {
      map.set(key, raw);
    }
  });
  return Array.from(map.values());
};

const RowContext = React.createContext<{ setActivatorNodeRef?: (el: HTMLElement | null) => void; listeners?: any }>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <HolderOutlined ref={setActivatorNodeRef} {...listeners} style={{ cursor: 'move', color: '#999' }} />
  );
};

const RowComponent = (props: any) => {
  const { children, ...restProps } = props;
  const record = children?.[0]?.props?.record || {};
  const key = String(record?.id ?? restProps['data-row-key'] ?? 'no-key');
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: key });
  const style: React.CSSProperties = {
    ...restProps.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };
  const value = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);
  return (
    <RowContext.Provider value={value}>
      <tr {...restProps} ref={setNodeRef} style={style} {...attributes}>
        {children}
      </tr>
    </RowContext.Provider>
  );
};

const extractOptionItems = (payload: any, options?: { skipGroups?: boolean }): Array<{ id: number; name?: string }> => {
  const resolved = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
  const results: Array<{ id: number; name?: string }> = [];
  const visit = (items: any[]) => {
    items.forEach((item) => {
      const id = Number(item?.id);
      const name = item?.name ?? item?.description ?? '';
      const isGroup = Boolean(item?.isGroup);
      if (Number.isFinite(id) && (!options?.skipGroups || !isGroup)) {
        if (!results.some((existing) => existing.id === id)) {
          results.push({ id, name });
        }
      }
      if (Array.isArray(item?.children)) {
        visit(item.children);
      }
    });
  };
  visit(resolved);
  return results;
};

interface QuestionListProps {
  referenceId?: number;
  reportFormatId?: number;
  toolId?: number;
  attributeId?: number;
  nodeTitle?: string;
  reportAttributeId?: number;
}

export default function QuestionList({
  referenceId,
  reportFormatId,
  toolId,
  attributeId,
  nodeTitle,
  reportAttributeId,
}: QuestionListProps) {
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const isReportNodeQuestionList = referenceId !== undefined;
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [reportFormatFunctionTags, setReportFormatFunctionTags] = useState<{ attribute: StaticFunctionTag[]; tool: StaticFunctionTag[] }>({ attribute: [], tool: [] });
  const [reportFormatQuestionTags, setReportFormatQuestionTags] = useState<string[]>([]);
  const [contextAttributeFunctionTags, setContextAttributeFunctionTags] = useState<StaticFunctionTag[]>([]);
  const [contextToolFunctionTags, setContextToolFunctionTags] = useState<StaticFunctionTag[]>([]);
  const [toolOptions, setToolOptions] = useState<Array<{ id: number; name?: string }>>([]);
  const [attributeOptions, setAttributeOptions] = useState<Array<{ id: number; name?: string }>>([]);
  const [selectedToolFilterId, setSelectedToolFilterId] = useState<number | undefined>(undefined);
  const [selectedAttributeFilterId, setSelectedAttributeFilterId] = useState<number | undefined>(undefined);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingTitleId, setEditingTitleId] = useState<number | null>(null);
  const [titleDraft, setTitleDraft] = useState<string>('');
  const [savingTitleId, setSavingTitleId] = useState<number | null>(null);
  const fetchRequestIdRef = useRef(0);
  const currentYear = new Date().getFullYear();
  const effectiveToolId = toolId ?? selectedToolFilterId;
  const effectiveAttributeId = attributeId ?? selectedAttributeFilterId;
  const questionTypeMap: Record<string, { label: string; color: string }> = {
    MULTIPLE: { label: 'Çoktan Seçmeli', color: 'blue' },
    FREETEXT: { label: 'Serbest Metin', color: 'purple' },
  NETWORK_TYPE: { label: 'Şebeke Tipi', color: 'cyan' },
    TITLE: { label: 'Ünvan', color: 'default' },
    ADDRESS: { label: 'Adres', color: 'default' },
    SGK_REGISTRATION_NUMBER: { label: 'SGK Sicil Numarası', color: 'default' },
    PHONE: { label: 'Telefon', color: 'default' },
    CONTRACT_ID: { label: 'ISG Katip Sözleşme ID', color: 'default' },
    EMAIL: { label: 'E-posta', color: 'default' },
    NUMBER: { label: 'Sayı', color: 'green' },
    DATE: { label: 'Tarih', color: 'gold' },
    TIME: { label: 'Saat', color: 'geekblue' },
    YEAR: { label: 'Yıl', color: 'cyan' },
    IMAGE: { label: 'Görsel', color: 'magenta' },
    HVAC: { label: 'Havalandırma/Klima', color: 'orange' },
    PLUMBING: { label: 'Tesisat', color: 'volcano' },
    HYDRANT: { label: 'Hidrant', color: 'red' },
    CABINET: { label: 'Dolap', color: 'magenta' },
    FUNCTION: { label: 'Fonksiyon', color: 'lime' },
    FIRST_NAME: { label: 'Ad', color: 'cyan' },
    LAST_NAME: { label: 'Soyad', color: 'cyan' },
    PROFESSION: { label: 'Meslek', color: 'cyan' },
    DIPLOMA_DATE: { label: 'Diploma Tarihi', color: 'cyan' },
    DIPLOMA_NUMBER: { label: 'Diploma Numarası', color: 'cyan' },
    REGISTRATION_NUMBER: { label: 'Kalıcı Kayıt Numarası', color: 'cyan' },
    IDENTITY_NUMBER: { label: 'TC Kimlik No', color: 'cyan' },
    REGISTRATION_DATE: { label: 'Kalıcı Kayıt Tarihi', color: 'cyan' },
    WORK_EQUIPMENT: { label: 'İş Ekipmanı', color: 'cyan' },
    REPORT_NUMBER: { label: 'Rapor Numarası', color: 'default' },
    REPORT_DATE: { label: 'Rapor Tarihi', color: 'gold' },
    CONTROL_START_DATE: { label: 'Kontrol Başlangıç Tarihi', color: 'gold' },
    CONTROL_END_DATE: { label: 'Kontrol Bitiş Tarihi', color: 'gold' },
    NEXT_CONTROL_DATE: { label: 'Sonraki Kontrol Tarihi', color: 'gold' },
    STARTED_AT: { label: 'Başlangıç Saati', color: 'geekblue' },
    FINISHED_AT: { label: 'Bitiş Saati', color: 'geekblue' },
    TABLE: { label: 'Tablo Sorusu', color: 'blue' },
  };
  const normalizeQuestionTypeKey = (value: any) => {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value).replace(/[^A-Za-z0-9_]/g, '').toUpperCase();
  };
  const toolNameMap = useMemo(() => {
    const map = new Map<number, string>();
    toolOptions.forEach((item) => {
      const id = Number(item?.id);
      if (!Number.isFinite(id)) {
        return;
      }
      const name = String(item?.name ?? `#${id}`);
      if (!map.has(id)) {
        map.set(id, name);
      }
    });
    return map;
  }, [toolOptions]);
  const attributeNameMap = useMemo(() => {
    const map = new Map<number, string>();
    attributeOptions.forEach((item) => {
      const id = Number(item?.id);
      if (!Number.isFinite(id)) {
        return;
      }
      const name = String(item?.name ?? `#${id}`);
      if (!map.has(id)) {
        map.set(id, name);
      }
    });
    return map;
  }, [attributeOptions]);
  const toolFilterOptions = useMemo(
    () =>
      toolOptions
        .map((item) => ({
          value: Number(item?.id),
          label: String(item?.name ?? `#${item?.id}`),
        }))
        .filter((option) => Number.isFinite(option.value)),
    [toolOptions],
  );
  const attributeFilterOptions = useMemo(
    () =>
      attributeOptions
        .map((item) => ({
          value: Number(item?.id),
          label: String(item?.name ?? `#${item?.id}`),
        }))
        .filter((option) => Number.isFinite(option.value)),
    [attributeOptions],
  );
  const combinedAttributeFunctionTags = useMemo(
    () => mergeStaticFunctionTags(reportFormatFunctionTags.attribute, contextAttributeFunctionTags),
    [reportFormatFunctionTags.attribute, contextAttributeFunctionTags],
  );
  const combinedToolFunctionTags = useMemo(
    () => mergeStaticFunctionTags(reportFormatFunctionTags.tool, contextToolFunctionTags),
    [reportFormatFunctionTags.tool, contextToolFunctionTags],
  );
  const resolveNumericValue = useCallback((value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }, []);
  const fetchData = useCallback(async () => {
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    setLoading(true);
    const queryParts: string[] = [];
    if (isReportNodeQuestionList) queryParts.push('parentQuestionId.specified=false');
    if (referenceId) queryParts.push('nodeId.equals=' + referenceId);
    if (effectiveToolId) queryParts.push('toolId.equals=' + effectiveToolId);
    if (effectiveAttributeId) queryParts.push('attributeId.equals=' + effectiveAttributeId);
    const query = queryParts.length ? queryParts.join('&') : undefined;
    try {
      if (isReportNodeQuestionList) {
        const questions = await listQuestionsUnpaged(query, 'sortOrder,asc');
        if (fetchRequestIdRef.current !== requestId) {
          return;
        }
        setData(questions);
        setTotal(questions.length);
      } else {
        const questionPage = await listQuestions(page - 1, pageSize, 'sortOrder,asc', query);
        if (fetchRequestIdRef.current !== requestId) {
          return;
        }
        setData(questionPage.content);
        setTotal(questionPage.totalElements);
      }
    } catch (error) {
      console.error('Sorular listelenemedi:', error);
      if (fetchRequestIdRef.current === requestId) {
        openNotification('error', 'Hata', 'Sorular listelenemedi');
      }
    } finally {
      if (fetchRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [referenceId, effectiveToolId, effectiveAttributeId, page, pageSize, isReportNodeQuestionList]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    setPage(1);
  }, [effectiveToolId, effectiveAttributeId, referenceId]);
  const loadReportFormatFunctionTags = useCallback(
    async (options?: { cancelled?: () => boolean }) => {
      if (!reportFormatId) {
        if (options?.cancelled?.()) {
          return;
        }
        setReportFormatFunctionTags({ attribute: [], tool: [] });
        setReportFormatQuestionTags([]);
        return;
      }
      try {
        const response = await getReportFormatFunctionTags(reportFormatId);
        if (options?.cancelled?.()) {
          return;
        }
        setReportFormatFunctionTags({
          attribute: mapStaticValuesToTags(response?.attributeStaticValues),
          tool: mapStaticValuesToTags(response?.toolStaticValues),
        });
        setReportFormatQuestionTags(normalizeFunctionTags(response?.questionFunctionTags));
      } catch (error) {
        console.error('Fonksiyon etiketleri yüklenemedi:', error);
        if (options?.cancelled?.()) {
          return;
        }
        setReportFormatFunctionTags({ attribute: [], tool: [] });
        setReportFormatQuestionTags([]);
      }
    },
    [reportFormatId],
  );
  useEffect(() => {
    let cancelled = false;
    loadReportFormatFunctionTags({ cancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
  }, [loadReportFormatFunctionTags]);
  useEffect(() => {
    let cancelled = false;
    ToolService.getAllTools()
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        setToolOptions(extractOptionItems(response));
      })
      .catch((error) => {
        console.error('Bileşen listesi yüklenemedi:', error);
        if (!cancelled) {
          setToolOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    AttributeService.getAll()
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        setAttributeOptions(extractOptionItems(response, { skipGroups: true }));
      })
      .catch((error) => {
        console.error('Parametre listesi yüklenemedi:', error);
        if (!cancelled) {
          setAttributeOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (!effectiveToolId) {
      setContextToolFunctionTags([]);
      return;
    }
    let cancelled = false;
    apiFetch(`/tools/${effectiveToolId}/static-values`)
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        const normalized = Array.isArray(response) ? response : response?.data;
        setContextToolFunctionTags(mapStaticValuesToTags(normalized));
      })
      .catch((error) => {
        console.error('Bileşen sabit değerleri yüklenemedi:', error);
        if (!cancelled) {
          setContextToolFunctionTags([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveToolId]);
  useEffect(() => {
    if (!effectiveAttributeId) {
      setContextAttributeFunctionTags([]);
      return;
    }
    let cancelled = false;
    apiFetch(`/attributes/${effectiveAttributeId}/static-values`)
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        const normalized = Array.isArray(response) ? response : response?.data;
        setContextAttributeFunctionTags(mapStaticValuesToTags(normalized));
      })
      .catch((error) => {
        console.error('Parametre sabit değerleri yüklenemedi:', error);
        if (!cancelled) {
          setContextAttributeFunctionTags([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveAttributeId]);
  const openCreate = () => {
    setEditingId(undefined);
    setOpen(true);
  };
  const openEdit = (id: number) => {
    setEditingId(id);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setEditingId(undefined);
  };
  const handleToolFilterSelect = useCallback(
    (value: unknown) => {
      const numeric = resolveNumericValue(value);
      setSelectedToolFilterId(numeric);
      setPage(1);
    },
    [resolveNumericValue],
  );
  const handleAttributeFilterSelect = useCallback(
    (value: unknown) => {
      const numeric = resolveNumericValue(value);
      setSelectedAttributeFilterId(numeric);
      setPage(1);
    },
    [resolveNumericValue],
  );
  const showToolFilter = toolId === undefined;
  const showAttributeFilter = attributeId === undefined;
  const extraContent = (
    <Space size={8} wrap>
      {/* {showToolFilter && (
        <Select
          allowClear
          placeholder='Bileşen seçiniz'
          style={{ minWidth: 180 }}
          value={selectedToolFilterId}
          options={toolFilterOptions}
          showSearch
          optionFilterProp='label'
          filterOption={(input, option) => (option?.label ?? '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
          onChange={handleToolFilterSelect}
        />
      )}
      {showAttributeFilter && (
        <Select
          allowClear
          placeholder='Parametre seçiniz'
          style={{ minWidth: 180 }}
          value={selectedAttributeFilterId}
          options={attributeFilterOptions}
          showSearch
          optionFilterProp='label'
          filterOption={(input, option) => (option?.label ?? '').toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))}
          onChange={handleAttributeFilterSelect}
        />
      )} */}
      <Button type='primary' onClick={openCreate}>Ekle</Button>
    </Space>
  );
  const availableFunctionTags = useMemo(() => {
    const map = new Map<string, string>();
    const pushTag = (value: unknown) => {
      const raw = String(value ?? '').trim();
      if (!raw) {
        return;
      }
      const key = raw.toUpperCase();
      if (!map.has(key)) {
        map.set(key, raw);
      }
    };
    data.forEach((item: any) => pushTag(item?.functionTag));
    reportFormatQuestionTags.forEach(pushTag);
    return Array.from(map.values());
  }, [data, reportFormatQuestionTags]);
  const handleDelete = async (id: number) => {
    try {
      await deleteQuestion(id);
      openNotification('success', 'Başarılı', 'Soru Silindi');
      fetchData();
      loadReportFormatFunctionTags();
    } catch (e) {
      const message = e instanceof Error && e.message ? e.message : 'Soru silme işlemi başarısız';
      openNotification('error', 'Hata', message);
    }
  };
  const handleClone = async (id: number) => {
    try {
      await cloneQuestion(id);
      openNotification('success', 'Başarılı', 'Soru kopyalandı');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Soru kopyalanamadı');
    }
  };
  const handleQuestionFormSuccess = (result: QuestionFormSuccessPayload) => {
    fetchData();
    loadReportFormatFunctionTags();
    const mode = result?.mode;
    const type = String(result?.questionType ?? '').toUpperCase();
    const newId = result?.id !== undefined && result?.id !== null ? Number(result.id) : undefined;

    if (!editingId && mode === 'create' && ['MULTIPLE', 'FUNCTION', 'TABLE'].includes(type) && newId) {
      setEditingId(newId);
      setOpen(true);
      return;
    }

    handleClose();
  };

  // Drag & drop ordering
  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = data.findIndex((item: any) => String(item.id) === String(active.id));
    const newIndex = data.findIndex((item: any) => String(item.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const newData = arrayMove(data, oldIndex, newIndex);
    const reordered = newData.map((item: any, index: number) => ({ id: item.id, sortOrder: index + 1 }));
    setData(newData.map((item: any, index: number) => ({ ...item, sortOrder: index + 1 })));
    try {
      await reorderQuestions(reordered);
      openNotification('success', 'Başarılı', 'Sıralama güncellendi');
    } catch (e) {
      openNotification('error', 'Hata', 'Sıralama güncellenemedi');
      fetchData();
    }
  };
  const sortableItems = useMemo(() => data.map((d: any) => String(d.id)), [data]);
  const tableComponents = useMemo(() => ({ body: { row: RowComponent } }), []);
  const buildQuestionUpdatePayload = useCallback((question: any, overrides: Partial<any> = {}) => {
    const nodeRaw = question?.nodeId ?? question?.node;
    const nodeValue = typeof nodeRaw === 'object' ? nodeRaw?.id : nodeRaw;
    const nodeId =
      nodeValue !== undefined && nodeValue !== null && nodeValue !== ''
        ? Number(nodeValue)
        : undefined;
    const colValue =
      question?.col !== undefined && question?.col !== null && question?.col !== ''
        ? Number(question.col)
        : undefined;
    const titleColValue =
      question?.titleCol !== undefined && question?.titleCol !== null && question?.titleCol !== ''
        ? Number(question.titleCol)
        : undefined;
    const sortOrderValue =
      question?.sortOrder !== undefined && question?.sortOrder !== null && question?.sortOrder !== ''
        ? Number(question.sortOrder)
        : undefined;
    const digitsValue =
      question?.digits !== undefined && question?.digits !== null && question?.digits !== ''
        ? Number(question.digits)
        : undefined;
    const answersRaw = Array.isArray(question?.answers)
      ? question.answers.map((answer: any) => (typeof answer === 'object' ? answer?.id : answer))
      : undefined;
    const controlAreasRaw = Array.isArray(question?.controlAreas)
      ? question.controlAreas.map((controlArea: any) => (typeof controlArea === 'object' ? controlArea?.id : controlArea))
      : undefined;
    const toolIdValue = resolveNumericValue(
      question?.toolId ?? (typeof question?.tool === 'object' ? question?.tool?.id : question?.tool),
    );
    const attributeIdValue = resolveNumericValue(
      question?.attributeId ?? (typeof question?.attribute === 'object' ? question?.attribute?.id : question?.attribute),
    );
    return {
      sortOrder: sortOrderValue,
      title: question?.title ?? '',
      description: question?.description ?? undefined,
      col: colValue,
      titleCol: titleColValue,
      isRequired: question?.isRequired ?? false,
      isCritical: question?.isCritical ?? false,
      isChecked: question?.isChecked ?? false,
      isVisible: question?.isVisible ?? false,
      excludeFromInspectorForm: question?.excludeFromInspectorForm ?? false,
      tag: question?.tag ?? undefined,
      isDistinctive: question?.isDistinctive ?? false,
      allowMultipleAnswers: question?.allowMultipleAnswers ?? false,
      showAllAnswersInReport: question?.questionType === 'MULTIPLE' ? question?.showAllAnswersInReport ?? false : false,
      countable: question?.countable ?? false,
      bulkInputMode: question?.bulkInputMode ?? 'PER_REPORT',
      checkReminderText: question?.checkReminderText ?? undefined,
      functionText: question?.functionText ?? undefined,
      functionTag: question?.functionTag ?? undefined,
      questionType: question?.questionType,
      minValue: question?.minValue ?? undefined,
      maxValue: question?.maxValue ?? undefined,
      isCurrentYear: question?.isCurrentYear ?? false,
      digits: digitsValue,
      node: nodeId,
      answers: answersRaw,
      controlAreas: controlAreasRaw,
      toolId: toolIdValue,
      attributeId: attributeIdValue,
      ...overrides,
    };
  }, [resolveNumericValue]);

  const startEditTitle = useCallback((record: any) => {
    const id = Number(record?.id);
    if (!Number.isFinite(id)) {
      return;
    }
    setEditingTitleId(id);
    setTitleDraft(record?.title ?? '');
  }, []);

  const cancelEditTitle = useCallback(() => {
    setEditingTitleId(null);
    setTitleDraft('');
  }, []);

  const handleTitleInputChange = useCallback((value: string) => {
    setTitleDraft(value);
  }, []);

  const handleTitleSave = useCallback(async (questionId: number) => {
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      openNotification('warning', 'Uyarı', 'Başlık boş bırakılamaz.');
      return;
    }
    const question = data.find((item: any) => Number(item?.id) === questionId);
    if (!question) {
      openNotification('error', 'Hata', 'Soru bulunamadı.');
      return;
    }
    const currentTitle = String(question?.title ?? '');
    if (trimmed === currentTitle) {
      cancelEditTitle();
      return;
    }
    const payload = buildQuestionUpdatePayload(question, { title: trimmed });
    if (!payload.col || !payload.questionType || !payload.node) {
      openNotification('error', 'Hata', 'Soru güncellenirken zorunlu alanlar eksik.');
      return;
    }
    setSavingTitleId(questionId);
    try {
      await updateQuestion(questionId, payload);
      setData((prev) =>
        prev.map((item: any) =>
          Number(item?.id) === questionId ? { ...item, title: trimmed } : item,
        ),
      );
      openNotification('success', 'Başarılı', 'Soru başlığı güncellendi.');
      cancelEditTitle();
    } catch (error) {
      console.error('Başlık güncelleme hatası:', error);
      openNotification('error', 'Hata', 'Soru başlığı güncellenemedi.');
    } finally {
      setSavingTitleId(null);
    }
  }, [buildQuestionUpdatePayload, cancelEditTitle, data, titleDraft]);

  const columns = [
    {
      title: '#',
      key: 'rowNumber',
      width: 56,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => isReportNodeQuestionList ? index + 1 : (page - 1) * pageSize + index + 1,
    },
    {
      key: 'drag',
      width: 40,
      align: 'center' as const,
      render: () => <DragHandle />,
    },
    {
      title: 'Soru Tipi', dataIndex: 'questionType', sorter: true, render: (v: any) => {
        const normalizedKey = normalizeQuestionTypeKey(v);
        const t = questionTypeMap[normalizedKey];
        if (t) {
          return <Tag color={t.color}>{t.label}</Tag>;
        }
        return <Tag>{String(v ?? '-')}</Tag>;
      }
    },
    {
      title: 'Başlık',
      dataIndex: 'title',
      sorter: true,
      render: (_: any, record: any) => {
        const id = Number(record?.id);
        if (!Number.isFinite(id)) {
          return record?.title ?? '';
        }
        if (editingTitleId === id) {
          const isSaving = savingTitleId === id;
          return (
            <Space wrap size={8}>
              <Input
                autoFocus
                value={titleDraft}
                onChange={(e) => handleTitleInputChange(e.target.value)}
                onPressEnter={() => handleTitleSave(id)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={isSaving}
                style={{ minWidth: 300 }}
              />
              <Button
                size="small"
                onClick={() => handleTitleSave(id)}
                loading={isSaving}
                disabled={!titleDraft.trim()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                Kaydet
              </Button>
              <Button
                size="small"
                onClick={cancelEditTitle}
                disabled={isSaving}
                onMouseDown={(e) => e.stopPropagation()}
              >
                İptal
              </Button>
            </Space>
          );
        }
        return (
          <Space size={8}>
            <span>{record?.title ?? ''}</span>
            <Tooltip title='Düzenle'>
              <Button
                type='text'
                size='small'
                icon={<img src={PenIcon} alt="edit" className="icon" />}
                onClick={(e) => {
                  e.stopPropagation();
                  startEditTitle(record);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Fonksiyon Etiketi',
      dataIndex: 'functionTag',
    },
    {
      title: 'Bileşen',
      dataIndex: 'tool',
      render: (_: any, record: any) => {
        const raw = record?.tool;
        const numericId = resolveNumericValue(record?.toolId ?? (typeof raw === 'object' ? raw?.id : raw));
        if (numericId === undefined) {
          return typeof raw === 'object' ? raw?.name ?? '' : '';
        }
        return toolNameMap.get(numericId) ?? (typeof raw === 'object' ? raw?.name ?? '' : '');
      },
    },
    {
      title: 'Parametre',
      dataIndex: 'attribute',
      render: (_: any, record: any) => {
        const raw = record?.attribute;
        const numericId = resolveNumericValue(record?.attributeId ?? (typeof raw === 'object' ? raw?.id : raw));
        if (numericId === undefined) {
          return typeof raw === 'object' ? raw?.name ?? '' : '';
        }
        return attributeNameMap.get(numericId) ?? (typeof raw === 'object' ? raw?.name ?? '' : '');
      },
    },
    { title: 'Kolon', dataIndex: 'col', sorter: true },
    { title: 'Başlık Sütunu', dataIndex: 'titleCol', sorter: true },
    { title: 'Zorunlu', dataIndex: 'isRequired', sorter: true, render: (v: boolean) => v ? <CheckmarkCircle02Icon style={{ color: '#52c41a', fontSize: 18 }} /> : '' },
    { title: 'Kritik', dataIndex: 'isCritical', sorter: true, render: (v: boolean) => v ? <CheckmarkCircle02Icon style={{ color: '#52c41a', fontSize: 18 }} /> : '' },
    {
      title: 'Min/Max',
      render: (_: any, r: any) => {
        const minFilled = r?.minValue !== undefined && r?.minValue !== null && r?.minValue !== '';
        const maxFilled = r?.maxValue !== undefined && r?.maxValue !== null && r?.maxValue !== '';
        if (r?.questionType === 'DATE' && r?.isCurrentDay) {
          return 'Bugün';
        }
        if (r?.isCurrentYear) {
          if (minFilled) {
            return `${r.minValue}/${currentYear}`;
          }
          return String(currentYear);
        }
        if (minFilled && maxFilled) return `${r.minValue}/${r.maxValue}`;
        return '';
      }
    },
    {
      title: 'İşlemler',
      render: (_: any, question: any) => (
        <QuestionActions
          question={question}
          onClone={handleClone}
          onEdit={(item) => openEdit(Number(item.id))}
          onDelete={handleDelete}
        />
      ),
    }
  ];
  const cardTitleText = nodeTitle ? `${nodeTitle} - Soruları ` : 'Soru';
  return (
    <>

      <Card
        className='br-30px'
        title={
          <Row align="bottom">
            <Typography.Title level={4} style={{ margin: 0 }}>{cardTitleText}</Typography.Title>
            <Text type="secondary" className="ms-5" ><strong>({total})</strong> kayıt listelendi</Text>
          </Row>
        }
        extra={extraContent}
      >
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          onDragStart={({ active }: any) => setActiveId(String(active.id))}
        >
          <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
            <Table
              size='small'
              className='table-compact'
              rowKey='id'
              dataSource={data}
              columns={columns}
              loading={loading}
              pagination={isReportNodeQuestionList ? false : { current: page, pageSize, total, onChange: (p, s) => { setPage(p); setPageSize(s); } }}
              components={tableComponents}
            />
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <div style={{ padding: '4px 8px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 4 }}>
                {data.find((d: any) => String(d.id) === String(activeId))?.title}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Card>
      <QuestionEditorDrawer
        open={open}
        questionId={editingId}
        onClose={handleClose}
        referenceId={referenceId}
        reportFormatId={reportFormatId}
        reportAttributeId={reportAttributeId}
        attributeFunctionTags={combinedAttributeFunctionTags}
        toolFunctionTags={combinedToolFunctionTags}
        availableFunctionTags={availableFunctionTags}
        toolId={effectiveToolId}
        attributeId={effectiveAttributeId}
        onSuccess={handleQuestionFormSuccess}
      />
    </>
  );
}
